/**
 * Unified debug logging system for Talousakatemia
 * 
 * Categories:
 * - SESSION: Session creation, restoration, logout
 * - MONEY: Balance changes, rewards, purchases
 * - PROGRESS: Module/question completion, achievements
 * - NAV: Route changes, location visits
 * - DATA: Data fetching, cache hits/misses
 * - ERROR: Errors (always shown)
 * 
 * Usage:
 * - Normal browsing: console is clean
 * - ?debug=1: Shows all debug logs
 * - ?debug=SESSION,MONEY: Shows only specific categories
 * 
 * Errors always show regardless of debug flag.
 */

export type DebugCategory = 
  | 'SESSION'   // Session lifecycle
  | 'MONEY'     // Financial transactions
  | 'PROGRESS'  // Game progress
  | 'NAV'       // Navigation
  | 'DATA'      // Data fetching
  | 'NETWORK'   // Network status, retries
  | 'ERROR';    // Errors (always shown)

// Parse debug settings from URL once at load time
const getDebugConfig = (): { enabled: boolean; categories: Set<DebugCategory> | 'all' } => {
  if (typeof window === 'undefined') {
    return { enabled: false, categories: new Set() };
  }
  
  try {
    const params = new URLSearchParams(window.location.search);
    const debugParam = params.get('debug');
    
    if (!debugParam) {
      return { enabled: false, categories: new Set() };
    }
    
    if (debugParam === '1' || debugParam === 'true' || debugParam === 'all') {
      return { enabled: true, categories: 'all' };
    }
    
    // Parse comma-separated categories
    const categories = new Set(
      debugParam.split(',').map(c => c.trim().toUpperCase()) as DebugCategory[]
    );
    return { enabled: categories.size > 0, categories };
  } catch {
    return { enabled: false, categories: new Set() };
  }
};

const DEBUG_CONFIG = getDebugConfig();

/**
 * Check if a category should be logged
 */
const shouldLog = (category: DebugCategory): boolean => {
  if (category === 'ERROR') return true; // Errors always show
  if (!DEBUG_CONFIG.enabled) return false;
  if (DEBUG_CONFIG.categories === 'all') return true;
  return DEBUG_CONFIG.categories.has(category);
};

/**
 * Format a debug message with category prefix
 */
const formatMessage = (category: DebugCategory, message: string): string => {
  return `[${category}] ${message}`;
};

/**
 * Log a debug message for a specific category
 */
export const debug = (
  category: DebugCategory,
  message: string,
  data?: unknown
): void => {
  if (!shouldLog(category)) return;
  
  const formatted = formatMessage(category, message);
  if (data !== undefined) {
    console.log(formatted, data);
  } else {
    console.log(formatted);
  }
};

/**
 * Log a SESSION event (session lifecycle)
 */
export const debugSession = (message: string, data?: unknown): void => {
  debug('SESSION', message, data);
};

/**
 * Log a MONEY event (financial transactions)
 */
export const debugMoney = (message: string, data?: unknown): void => {
  debug('MONEY', message, data);
};

/**
 * Log a PROGRESS event (game progress)
 */
export const debugProgress = (message: string, data?: unknown): void => {
  debug('PROGRESS', message, data);
};

/**
 * Log a NAV event (navigation)
 */
export const debugNav = (message: string, data?: unknown): void => {
  debug('NAV', message, data);
};

/**
 * Log a DATA event (data fetching)
 */
export const debugData = (message: string, data?: unknown): void => {
  debug('DATA', message, data);
};

/**
 * Log a NETWORK event (network status, retries)
 */
export const debugNetwork = (message: string, data?: unknown): void => {
  debug('NETWORK', message, data);
};

/**
 * Log an error (always shown)
 */
export const debugError = (message: string, error?: unknown): void => {
  console.error(`[ERROR] ${message}`, error ?? '');
};

/**
 * Check if debug mode is enabled for any category
 */
export const isDebugEnabled = (): boolean => DEBUG_CONFIG.enabled;

/**
 * Check if a specific category is being logged
 */
export const isCategoryEnabled = (category: DebugCategory): boolean => shouldLog(category);
